import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    closeDetailPanel,
    setSelectedOrganizer,
    toggleEditModal,
    setEditedOrganizer,
    toggleNeedRefresh,
} from '../../../store/organizersSlice';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import { ReactComponent as ChartIcon } from '../../icons/chart.svg';
import { ReactComponent as TrashIcon } from '../../icons/trash.svg';
import { ReactComponent as LockIcon } from '../../icons/lock.svg';
import { ReactComponent as UnlockIcon } from '../../icons/unlock.svg';
import { ReactComponent as EditIcon } from '../../icons/edit.svg';
import { ReactComponent as CheckIcon } from '../../icons/tick-circle.svg';
import { ReactComponent as CloseCircleIcon } from '../../icons/close-circle.svg';
import ApiService from '../../api_services/apiService.jsx';
import { normalizeOrganizerProfile } from '../../../tables/organizers/organizersTableService.js';
import './OrganizerDetailPanel.css';

function OrganizerDetailPanel({ allOrganizers = [] }) {
    const dispatch = useDispatch();
    const selectedOrganizer = useSelector((state) => state.organizers.selectedOrganizer);
    const detailPanelOpen = useSelector((state) => state.organizers.detailPanelOpen);
    const isPendingOrganizer = selectedOrganizer?.status === 'pending';

    useEffect(() => {
        let isMounted = true;

        const fetchOrganizerProfile = async () => {
            if (!selectedOrganizer?.id) return;

            try {
                const response = await ApiService.getOrganiserProfile(selectedOrganizer.id);
                const profile = normalizeOrganizerProfile(response?.data ?? response);
                if (isMounted && profile?.id) {
                    dispatch(setSelectedOrganizer(profile));
                }
            } catch (error) {
                console.error('Error fetching organizer profile:', error);
            }
        };

        fetchOrganizerProfile();

        return () => {
            isMounted = false;
        };
    }, [selectedOrganizer?.id, dispatch]);
    
    // Tab state: 'basic' or 'description'
    const [activeTab, setActiveTab] = useState('basic');
    
    // Find current index in all organizers
    const currentIndex = allOrganizers.findIndex(org => org.id === selectedOrganizer?.id);
    const totalCount = allOrganizers.length;
    
    // Navigation handlers
    const handlePrevious = useCallback(() => {
        if (currentIndex > 0) {
            dispatch(setSelectedOrganizer(allOrganizers[currentIndex - 1]));
        }
    }, [currentIndex, allOrganizers, dispatch]);
    
    const handleNext = useCallback(() => {
        if (currentIndex < allOrganizers.length - 1) {
            dispatch(setSelectedOrganizer(allOrganizers[currentIndex + 1]));
        }
    }, [currentIndex, allOrganizers, dispatch]);
    
    // Close handler
    const handleClose = useCallback(() => {
        dispatch(closeDetailPanel());
    }, [dispatch]);
    
    // Edit handler
    const handleEdit = useCallback(() => {
        if (selectedOrganizer) {
            dispatch(setEditedOrganizer(selectedOrganizer));
            dispatch(toggleEditModal());
        }
    }, [selectedOrganizer, dispatch]);
    
    // Delete handler
    const handleDelete = useCallback(() => {
        if (selectedOrganizer) {
            if (window.confirm(`Czy na pewno chcesz usunąć organizatora "${selectedOrganizer.companyName}"?`)) {
                console.log('Delete organizer:', selectedOrganizer.id);
                // TODO: Implement API call
                dispatch(closeDetailPanel());
            }
        }
    }, [selectedOrganizer, dispatch]);
    
    // Block/Unblock handler
    const handleToggleBlock = useCallback(() => {
        if (selectedOrganizer) {
            const action = selectedOrganizer.isBlocked ? 'odblokować' : 'zablokować';
            if (window.confirm(`Czy na pewno chcesz ${action} organizatora "${selectedOrganizer.companyName}"?`)) {
                console.log(selectedOrganizer.isBlocked ? 'Unblock' : 'Block', 'organizer:', selectedOrganizer.id);
                // TODO: Implement API call
            }
        }
    }, [selectedOrganizer]);
    
    // Password reset handler
    const handlePasswordReset = useCallback(() => {
        if (selectedOrganizer) {
            if (window.confirm(`Czy na pewno chcesz wysłać wiadomość z resetem hasła do "${selectedOrganizer.email}"?`)) {
                ApiService.requestPasswordRecovery(selectedOrganizer.email)
                    .then(() => {
                        alert('Wysłano wiadomość z resetem hasła.');
                    })
                    .catch((error) => {
                        console.error('Password reset request failed:', error);
                    });
            }
        }
    }, [selectedOrganizer]);
    
    // Statistics handler
    const handleStatistics = useCallback(() => {
        console.log('Show statistics for organizer:', selectedOrganizer?.id);
        // TODO: Navigate to statistics page
    }, [selectedOrganizer]);

    const handleAcceptPending = useCallback(async () => {
        if (!selectedOrganizer || selectedOrganizer.status !== 'pending') return;
        if (window.confirm(`Czy na pewno chcesz zaakceptować organizatora "${selectedOrganizer.companyName}"?`)) {
            try {
                await ApiService.approveOrganiser(selectedOrganizer.id);
                const response = await ApiService.getOrganiserProfile(selectedOrganizer.id);
                const profile = normalizeOrganizerProfile(response?.data ?? response);
                dispatch(setSelectedOrganizer(profile));
                dispatch(toggleNeedRefresh());
            } catch (error) {
                console.error('Error accepting organizer:', error);
            }
        }
    }, [selectedOrganizer, dispatch]);

    const handleRejectPending = useCallback(() => {
        if (!selectedOrganizer || selectedOrganizer.status !== 'pending') return;
        if (window.confirm(`Czy na pewno chcesz odrzucić organizatora "${selectedOrganizer.companyName}"?`)) {
            console.log('Reject organizer:', selectedOrganizer.id);
            // TODO: Implement API call
        }
    }, [selectedOrganizer]);
    
    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('pl-PL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).replace(/\//g, '-');
    };
    
    // Extract address parts
    const getAddressParts = (address) => {
        if (!address) return { street: '-', zipCode: '-', city: '-' };
        
        // Try to parse address like "ul. Główna 15, 30-001 Kraków"
        const parts = address.split(',').map(p => p.trim());
        const street = parts[0] || '-';
        
        if (parts[1]) {
            const cityParts = parts[1].trim().split(' ');
            const zipCode = cityParts[0] || '-';
            const city = cityParts.slice(1).join(' ') || '-';
            return { street, zipCode, city };
        }
        
        return { street, zipCode: '-', city: '-' };
    };
    
    if (!detailPanelOpen || !selectedOrganizer) {
        return null;
    }
    
    const addressParts = getAddressParts(selectedOrganizer.address);
    
    return (
        <>
            {/* Backdrop */}
            <div className="organizer-detail-backdrop" onClick={handleClose} />
            
            {/* Panel */}
            <div className="organizer-detail-panel">
            {/* Header */}
            <div className="organizer-detail-header">
                <div className="header-left">
                    <div className="navigation-arrows">
                        <button 
                            className="nav-arrow-btn"
                            onClick={handlePrevious}
                            disabled={currentIndex <= 0}
                            title="Poprzedni"
                        >
                            <ArrowLeftIcon className="nav-arrow-icon" />
                        </button>
                        <button 
                            className="nav-arrow-btn"
                            onClick={handleNext}
                            disabled={currentIndex >= totalCount - 1}
                            title="Następny"
                        >
                            <ArrowRightIcon className="nav-arrow-icon" />
                        </button>
                    </div>
                    <span className="results-counter">
                        {currentIndex + 1} z {totalCount} wyników
                    </span>
                </div>
                <div className="header-right">
                    <span className="registration-date">
                        Data dodania: {formatDate(selectedOrganizer.registrationDate)}
                    </span>
                </div>
            </div>
            
            {/* Main Content */}
            <div className="organizer-detail-content">
                {/* Company Name Section */}
                <div className="company-header-section">
                    <div className="company-info">
                        <div className="company-id-name">
                            <span className="company-id">(ID {selectedOrganizer.id})</span>
                            <h2 className="company-name">{selectedOrganizer.companyName}</h2>
                        </div>
                        <div className="company-actions">
                            {isPendingOrganizer ? (
                                <div className="pending-review-actions">
                                    <button
                                        className="header-action-btn header-action-outline-teal"
                                        onClick={handleRejectPending}
                                        type="button"
                                    >
                                        <CloseCircleIcon className="btn-icon" />
                                        <span>Odrzuć</span>
                                    </button>
                                    <button
                                        className="header-action-btn header-action-filled"
                                        onClick={handleAcceptPending}
                                        type="button"
                                    >
                                        <CheckIcon className="btn-icon" />
                                        <span>Zaakceptuj</span>
                                    </button>
                                </div>
                            ) : (
                                <button className="statistics-btn" onClick={handleStatistics} type="button">
                                    <ChartIcon className="btn-icon" />
                                    <span>Statystyki</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Tabs Navigation */}
                <div className="detail-tabs-nav">
                    <div className="tabs-container">
                        <button 
                            className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
                            onClick={() => setActiveTab('basic')}
                        >
                            Dane podstawowe
                        </button>
                        <button 
                            className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
                            onClick={() => setActiveTab('description')}
                        >
                            Opis
                        </button>
                    </div>
                </div>
                
                {/* Tab Content */}
                {activeTab === 'basic' && (
                    <div className="tab-content basic-data">
                        {/* ID Row */}
                        <div className="data-section">
                            <div className="data-row">
                                <div className="data-row-inner">
                                    <div className="data-label">ID</div>
                                    <div className="data-value">{selectedOrganizer.id}</div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Company Info Section */}
                        <div className="data-section">
                            <div className="data-row">
                                <div className="data-row-inner">
                                    <div className="data-label">Nazwa firmy</div>
                                    <div className="data-value">{selectedOrganizer.companyName}</div>
                                </div>
                            </div>
                            <div className="data-row">
                                <div className="data-row-inner">
                                    <div className="data-label">NIP</div>
                                    <div className="data-value">{selectedOrganizer.nip}</div>
                                </div>
                            </div>
                            <div className="data-row">
                                <div className="data-row-inner contact-row">
                                    <div className="data-label">Kontakt</div>
                                    <div className="data-value">{selectedOrganizer.email}</div>
                                    <button 
                                        className="password-reset-btn"
                                        onClick={handlePasswordReset}
                                    >
                                        Wiadomość z resetem hasła
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        {/* Address Section */}
                        <div className="data-section">
                            <div className="section-header">
                                <div className="data-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Adres</div>
                                    </div>
                                </div>
                            </div>
                            <div className="address-details">
                                <div className="data-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Ulica</div>
                                        <div className="data-value">{addressParts.street}</div>
                                    </div>
                                </div>
                                <div className="data-row two-column">
                                    <div className="data-row-inner">
                                        <div className="data-label">Kod pocztowy</div>
                                        <div className="data-value">{addressParts.zipCode}</div>
                                    </div>
                                    <div className="data-row-inner">
                                        <div className="data-label">Miejscowość</div>
                                        <div className="data-value">{addressParts.city}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {activeTab === 'description' && (
                    <div className="tab-content description-data">
                        {/* Description Section */}
                        <div className="data-section">
                            <div className="data-row description-row">
                                <div className="data-row-inner">
                                    <div className="data-label">Opis działalności</div>
                                    <div className="data-value description-text">
                                        {selectedOrganizer.description || 'Brak opisu działalności.'}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Links Section */}
                        <div className="data-section">
                            <div className="data-row">
                                <div className="data-row-inner">
                                    <div className="data-label">Link do Facebook</div>
                                    <div className="data-value">
                                        {selectedOrganizer.facebookUrl ? (
                                            <a 
                                                href={selectedOrganizer.facebookUrl} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="link-value"
                                            >
                                                {selectedOrganizer.facebookUrl}
                                            </a>
                                        ) : (
                                            <span className="no-value">-</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="data-row">
                                <div className="data-row-inner">
                                    <div className="data-label">Strona WWW</div>
                                    <div className="data-value">
                                        {selectedOrganizer.websiteUrl ? (
                                            <a 
                                                href={selectedOrganizer.websiteUrl.startsWith('http') ? selectedOrganizer.websiteUrl : `https://${selectedOrganizer.websiteUrl}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="link-value"
                                            >
                                                {selectedOrganizer.websiteUrl}
                                            </a>
                                        ) : (
                                            <span className="no-value">-</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Footer Actions */}
            <div className="organizer-detail-footer">
                <div className="footer-buttons">
                    <button className="action-btn outline danger" onClick={handleDelete}>
                        <TrashIcon className="btn-icon" />
                        <span>Usuń</span>
                    </button>
                    <button className="action-btn outline" onClick={handleToggleBlock}>
                        {selectedOrganizer.isBlocked ? (
                            <>
                                <UnlockIcon className="btn-icon" />
                                <span>Odblokuj</span>
                            </>
                        ) : (
                            <>
                                <LockIcon className="btn-icon" />
                                <span>Zablokuj</span>
                            </>
                        )}
                    </button>
                    <button className="action-btn filled" onClick={handleEdit}>
                        <EditIcon className="btn-icon" />
                        <span>Edytuj</span>
                    </button>
                </div>
            </div>
        </div>
        </>
    );
}

export default OrganizerDetailPanel;
